<template>
  <div :style="gridItemStyles" :class="classes" v-if="!draft">
    <slot />
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ComponentGridItem",
  props: {
    inAppAppearanceActive: {
      type: Boolean,
      default: false,
    },
    componentClass: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: "is-full",
    },
    gridAreas: {
      type: Object,
      default() {
        return {}
      }
    },
    draft: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    windowWidth: window.innerWidth,
  }),
  computed: {
    ...mapGetters({
      isAppContainer: "instancesState/getIsAppContainer",
    }),
    classes() {
      const classes = [
        "component-grid__item",
        this.inAppAppearanceActive && this.isAppContainer
          ? "in-app-griditem-style"
          : "",
          this.componentClass
      ];
      classes.push(`component-grid__item--is-${this.size}`);
      return classes;
    },
    gridItemStyles() {
      const styles = {};
      if (this.gridAreas.gridColumn && this.windowWidth >= 992) {
        styles.gridColumn = this.gridAreas.gridColumn;
      }
      if (this.gridAreas.gridColumnTablet && this.windowWidth >= 768 && this.windowWidth <= 991) {
        styles.gridColumn = this.gridAreas.gridColumnTablet;
      }
      if (this.gridAreas.gridColumnPhone && this.windowWidth <= 767) {
        styles.gridColumn = this.gridAreas.gridColumnPhone;
      }
      if (this.gridAreas.gridRow && this.windowWidth >= 992) {
        styles.gridRow = this.gridAreas.gridRow;
      }
      if (this.gridAreas.gridRowTablet && this.windowWidth >= 768 && this.windowWidth <= 991) {
        styles.gridRow = this.gridAreas.gridRowTablet;
      }
      if (this.gridAreas.gridRowPhone && this.windowWidth <= 767) {
        styles.gridRow = this.gridAreas.gridRowPhone;
      }
      if (this.gridAreas.gridArea) {
        styles.gridArea = this.gridAreas.gridArea;
      }
      
      return styles;
    },
  },
  created() {
    window.addEventListener('resize', this.onResize)
  },
  methods: {
    onResize() {
      this.windowWidth = window.innerWidth
    },
  },
};
</script>

<style lang="scss">
.component-grid__item:empty {
  margin: 0 !important;
}
// $c-grid-gutter: 10px;

.component-grid__item {
  width: 100%;
  margin: 0 0 0 0;
  flex: 0 0 100%;
  box-sizing: border-box;

  &:not(.component-grid__item--is-full) {
    width: 100%;
    margin: 0 0 0 0 !important;
    flex: 0 0 100%;
  }
}

/* making fullWidth components really full width */
.component-grid__container--full-width .component-grid__item {
  width: 100%;
  flex: 0 0 100%;
  margin-right: 0;
  flex-basis: 100%;
}

/* tablet */
@media (min-width: 768px) {
  .component-grid__item--is-one-third {
    flex: 0 0 calc(50% - calc(var(--link-box-grid-gap) * 0.5)) !important;
    width: calc(50% - calc(var(--link-box-grid-gap) * 0.5)) !important;
  }

  .component-grid__item--is-half {
    flex-basis: calc(50% - calc(var(--link-box-grid-gap) * 0.5)) !important;
    width: calc(50% - calc(var(--link-box-grid-gap) * 0.5)) !important;
  }
}

/* Desktop */
@media (min-width: 992px) {
  .component-grid__item--is-one-third {
    flex: 0 0
      calc(calc(100% / 3) - calc(var(--link-box-grid-gap) * calc(2 / 3))) !important;
    width: calc(
      calc(100% / 3) - calc(var(--link-box-grid-gap) * calc(2 / 3))
    ) !important;
  }
  .component-grid__item--is-one-quarter {
    flex: 0 0 calc(25% - calc(var(--link-box-grid-gap) * 0.75)) !important;
    width: calc(25% - calc(var(--link-box-grid-gap) * 0.75)) !important;
  }
  .component-grid__item--is-one-fifth {
    flex: 0 0 calc(20% - calc(var(--link-box-grid-gap) * 0.8)) !important;
    width: calc(20% - calc(var(--link-box-grid-gap) * 0.8)) !important;
  }
  .component-grid__item--is-one-sixth {
    flex: 0 0
      calc(calc(100% / 6) - calc(var(--link-box-grid-gap) * calc(5 / 6))) !important;
    width: calc(
      calc(100% / 6) - calc(var(--link-box-grid-gap) * calc(5 / 6))
    ) !important;
  }
  .component-grid__item--is-one-seventh {
    flex: 0 0
      calc(calc(100% / 7) - calc(var(--link-box-grid-gap) * calc(6 / 7))) !important;
    width: calc(calc(100% / 7) - calc(var(--link-box-grid-gap) * calc(6 / 7)));
  }

  .component-grid__item--is-one-eigth {
    flex: 0 0
      calc(calc(100% / 8) - calc(var(--link-box-grid-gap) * calc(7 / 8))) !important;
    width: calc(calc(100% / 8) - calc(var(--link-box-grid-gap) * calc(7 / 8)));
  }

  .component-grid__item--is-one-eigth {
    flex: 0 0
      calc(calc(100% / 8) - calc(var(--link-box-grid-gap) * calc(7 / 8))) !important;
    width: calc(calc(100% / 8) - calc(var(--link-box-grid-gap) * calc(7 / 8)));
  }

  .component-grid__item--is-two-thirds {
    flex-basis: calc(
      66.6666% - calc(var(--link-box-grid-gap) * calc(1 / 3))
    ) !important;
    width: calc(
      66.6666% - calc(var(--link-box-grid-gap) * calc(1 / 3))
    ) !important;
  }

  .component-grid__item--is-full {
    flex-basis: 100%;
    width: 100%;
  }

  .component-grid__item--is-three-quarters {
    flex: 0 0 calc(75% - calc(var(--link-box-grid-gap) * 0.25)) !important;
    width: calc(75% - calc(var(--link-box-grid-gap) * 0.25)) !important;
  }

  .component-grid__item--is-two-fifths {
    flex: 0 0 calc(40% - calc(var(--link-box-grid-gap) * 0.6)) !important;
    width: calc(40% - calc(var(--link-box-grid-gap) * 0.6)) !important;
  }
  .component-grid__item--is-three-fifths {
    flex: 0 0 calc(60% - calc(var(--link-box-grid-gap) * 0.4)) !important;
    width: calc(60% - calc(var(--link-box-grid-gap) * 0.4)) !important;
  }
  .component-grid__item--is-four-fifths {
    flex: 0 0 calc(80% - calc(var(--link-box-grid-gap) * 0.2)) !important;
    width: calc(80% - calc(var(--link-box-grid-gap) * 0.2)) !important;
  }
}
.in-app-griditem-style {
  width: 50% !important;
  margin: 0 0 0 0 !important;
  flex: 0 0 calc(50% - calc(var(--link-box-grid-gap) / 2)) !important;
}
</style>
